import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { ChevronLeft, Star, Share, UploadCloud } from 'lucide-react';

export default function JobDetails() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  
  // Application form state
  const [resume, setResume] = useState(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    fetchJobDetails();
    if (user) {
      checkIfApplied();
    }
  }, [jobId, user]);

  const fetchJobDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('hr_jobs')
        .select('*')
        .eq('id', jobId)
        .single();
      
      if (error) throw error;
      setJob(data);
    } catch (error) {
      console.error('Error fetching job details:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkIfApplied = async () => {
    try {
      const { data, error } = await supabase
        .from('hr_job_applications')
        .select('id')
        .eq('job_id', jobId)
        .eq('applicant_id', user.id)
        .single();
      
      if (data) setApplied(true);
    } catch (error) {
      // Not applied or error
    }
  };

  const handleApplyClick = () => {
    if (!user) {
      // Redirect to login, then back here
      navigate('/login', { state: { from: `/jobs/${jobId}` } });
      return;
    }
    setShowApplyModal(true);
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!resume) return;

    setSubmitting(true);
    try {
      // 1. Upload resume to Supabase Storage
      const fileExt = resume.name.split('.').pop();
      const fileName = `${user.id}_${Date.now()}.${fileExt}`;
      const filePath = `resumes/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('resumes')
        .upload(filePath, resume);

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('resumes')
        .getPublicUrl(filePath);

      // 2. Fetch user profile for name/email
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, email')
        .eq('id', user.id)
        .single();

      // 3. Create application record
      const { error: appError } = await supabase
        .from('hr_job_applications')
        .insert({
          job_id: job.id,
          applicant_id: user.id,
          first_name: profile?.first_name || '',
          last_name: profile?.last_name || '',
          email: profile?.email || user.email,
          resume_url: publicUrlData.publicUrl,
          cover_letter: coverLetter
        });

      if (appError) throw appError;

      setApplied(true);
      setShowApplyModal(false);
    } catch (error) {
      console.error('Application failed:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex justify-center bg-[#f5f5f7]">
        <div className="w-8 h-8 border-4 border-[#065DC0] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!job) {
    return <div className="min-h-screen pt-32 text-center text-xl">Job not found.</div>;
  }

  return (
    <div className="bg-[#fcfcfc] min-h-screen text-[#1d1d1f] pt-24 pb-20 font-sans">
      <div className="max-w-[800px] mx-auto px-4 sm:px-6">
        
        <button 
          onClick={() => navigate('/jobs')}
          className="flex items-center text-[#065DC0] hover:underline mb-12 font-medium"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to search results
        </button>

        <div className="text-center border-b border-gray-200 pb-12 mb-12">
          <h1 className="text-4xl md:text-5xl font-semibold mb-4 tracking-tight">{job.title}</h1>
          <div className="text-xl text-gray-700 mb-6 font-medium">
            <p>{job.location}</p>
            <p>{job.department}</p>
          </div>
          
          <div className="flex justify-center items-center gap-4 mb-8">
            <button className="text-[#065DC0] p-2 hover:bg-[#065DC0]/10 rounded-full transition-colors">
              <Star className="w-6 h-6" />
            </button>
            <button className="text-[#065DC0] p-2 hover:bg-[#065DC0]/10 rounded-full transition-colors">
              <Share className="w-6 h-6" />
            </button>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button 
              className="px-8 py-3 rounded-full border-2 border-[#065DC0] text-[#065DC0] font-semibold hover:bg-[#065DC0]/5 transition-colors"
            >
              Where we're hiring
            </button>
            <button 
              onClick={handleApplyClick}
              disabled={applied}
              className={`px-8 py-3 rounded-full font-semibold transition-colors ${
                applied 
                  ? 'bg-green-600 text-white cursor-default' 
                  : 'bg-[#065DC0] text-white hover:bg-[#0077ed]'
              }`}
            >
              {applied ? 'Application Submitted ✓' : 'Submit CV'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-8 mb-12">
          <div>
            <h2 className="text-xl font-semibold mb-2">Summary</h2>
            <div className="text-sm text-gray-500 font-medium">
              <p>Posted: {new Date(job.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
              <p>Role Number: 2003{job.id.substring(0, 5)}</p>
            </div>
          </div>
          <div className="prose prose-lg text-gray-700">
            {job.description.split('\n').map((para, i) => (
              <p key={i} className="mb-4">{para}</p>
            ))}
          </div>
        </div>

        <div className="border-t border-gray-200 pt-12 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-8 mb-12">
          <h2 className="text-xl font-semibold">Minimum Qualifications</h2>
          <div>
            <ul className="list-disc pl-5 space-y-3 text-gray-700 text-lg">
              {job.qualifications && job.qualifications.length > 0 ? (
                job.qualifications.map((q, i) => <li key={i}>{q}</li>)
              ) : (
                <>
                  <li>You should:</li>
                  <li>Have experience relevant to the role.</li>
                  <li>Be proficient in the local language, both written and spoken.</li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-12 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-8">
          <h2 className="text-xl font-semibold">Preferred Qualifications</h2>
          <div>
            <ul className="list-disc pl-5 space-y-3 text-gray-700 text-lg">
              {job.preferred_qualifications && job.preferred_qualifications.length > 0 ? (
                job.preferred_qualifications.map((q, i) => <li key={i}>{q}</li>)
              ) : (
                <>
                  <li>You can:</li>
                  <li>Show a track record of strong performance and results.</li>
                  <li>Communicate effectively and tailor your communication style to different audiences.</li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-20 text-center border-t border-gray-200 pt-12">
          <button 
            onClick={handleApplyClick}
            disabled={applied}
            className={`px-8 py-3 rounded-full font-semibold transition-colors ${
              applied 
                ? 'bg-green-600 text-white cursor-default' 
                : 'bg-[#065DC0] text-white hover:bg-[#0077ed]'
            }`}
          >
            {applied ? 'Application Submitted ✓' : 'Submit CV'}
          </button>
        </div>
      </div>

      {/* Apply Modal */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl relative">
            <button 
              onClick={() => setShowApplyModal(false)}
              className="absolute top-6 right-6 text-gray-400 hover:text-black"
            >
              ✕
            </button>
            
            <h2 className="text-2xl font-bold mb-2">Apply for {job.title}</h2>
            <p className="text-gray-500 mb-8">Submit your CV and optional cover letter.</p>
            
            <form onSubmit={submitApplication} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2">Resume / CV (PDF)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:bg-gray-50 transition-colors cursor-pointer relative">
                  <input 
                    type="file" 
                    accept=".pdf,.doc,.docx"
                    required
                    onChange={(e) => setResume(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <UploadCloud className="w-10 h-10 text-[#065DC0] mx-auto mb-3" />
                  {resume ? (
                    <p className="font-medium text-[#065DC0]">{resume.name}</p>
                  ) : (
                    <p className="text-gray-500">Drag and drop or click to upload</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">Cover Letter (Optional)</label>
                <textarea 
                  rows={4}
                  value={coverLetter}
                  onChange={(e) => setCoverLetter(e.target.value)}
                  className="w-full p-4 rounded-2xl border border-gray-300 focus:ring-2 focus:ring-[#065DC0] focus:border-transparent outline-none resize-none"
                  placeholder="Tell us why you're a great fit..."
                  style={{ borderRadius: '1rem' }}
                />
              </div>

              <button 
                type="submit" 
                disabled={submitting || !resume}
                className="w-full py-4 rounded-full bg-[#065DC0] text-white font-bold disabled:opacity-50 hover:bg-[#0077ed] transition-colors"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
